<template lang="pug">
y-dialog(v-if="showDialog" v-model="showDialog" persistent scrollable max-width="700px" content-class="round-2xl")
  v-card
    v-card-title.px-4
      v-spacer
      .text-xl.font-bold Add item to list
      v-spacer
      v-btn(icon color="neutral700" @click="handleCloseDialog")
        v-icon mdi-close
    
    v-divider
    
    v-card-text.neutral700--text
      .top-bar.pt-4
        .d-flex.justify-space-between.align-center.mb-4
          .text-md.font-semibold Search and add item
          template(v-if="inventories.totalCount > pageSize")
            yc-pagination-custom(v-model="currentPage" :parentPageSize.sync="pageSize" :count="inventories.totalCount" :loading="inventories.isFetching" attach menu-top @input="pageChanged") 
        v-text-field.text-sm.mt-2.round-xl(v-model="searchText" placeholder="Search by name, group or UOM" outlined dense height="48" hide-details="auto")
          template(#prepend-inner)
            v-icon.mt-2.mr-1(size="17") $yzi-search-sm
      v-data-table.mt-4.pb-6(v-model="selected" :headers="headers" :items="inventories.data" item-key="id" :items-per-page="pageSize" :loading="inventories.isFetching" hide-default-footer disable-sort loader-height="2" show-select :item-class="rowClass")
        template(v-slot:item.detail="{ item }")
          .my-3(:class="{ 'row-disabled': item.disabled }")
            .d-flex.align-center.justify-space-between
              .text-sm {{ item.detail.name }}
              yz-badge(v-if="isHqContext || item.detail.branch_for" x-small color="blue-100" text-color="blue-500" border) HQ
            .d-flex
              .d-flex.align-center.mr-4.no-wrap
                template(v-if="hasStock(item)")
                  v-icon.mr-1(size="10" color="success") $yzi-circle
                template(v-else)
                  v-icon.mr-1(size="10" color="danger") $yzi-circle
                .text-xs Available: {{ item.detail.available_qty }}
              .d-flex.align-center.mr-4.no-wrap
                .text-xs To order: {{ item.detail.to_order_qty }}
              .d-flex.align-center.mr-4(v-if="item.detail.uom")
                v-icon.mr-1(size="13") $yzi-colors
                .text-xs.font-normal.neutral500--text {{ item.detail.uom | capitalize }}
              .d-flex.align-center.mr-4(v-if="item.detail.groups && item.detail.groups.length > 0")
                v-icon.mr-1(size="13") $yzi-folder
                .text-xs.font-normal.neutral500--text {{ getGroupName(item) | capitalize }}
        template(v-slot:header.data-table-select="{ props }")
          yc-checkbox.ma-0(:input-value="allSelected" :indeterminate="props.indeterminate" :disabled="inventories.data.length === 0" @change="handleSelectAll")
        template(v-slot:item.data-table-select="{ item, select, isSelected }")
          yc-checkbox.ma-0(:input-value="isSelected" :disabled="item.disabled" @change="select($event)")
      
    v-card-actions.white.pa-0
      .d-flex.flex-column(style="width:100%")
        v-divider
        .d-flex.align-center.pa-4.justify-end
          v-btn.button-md.neutral700--text.underline.mr-2(text @click="handleCloseDialog") Cancel
          v-btn.button-md(:loading="isLoading" color="primary" depressed @click="handleAdd") Add ({{ selected.length }})
</template>

<script>
import { debounce } from '@/utils'
import { mapGetters, mapActions } from 'vuex'

export default {
  props: {
    open: {
      type: Boolean,
    },
    overrideCompanySlug: {
      type: String,
      default: null
    },
    selectedDocument: {
      type: Object,
      default: null
    },
  },
  data() {
    return {
      searchText: '',
      selected: [],
      inventories: {
        totalCount: 0,
        data: [],
        isFetching: false,
      },
      headers: [
        { text: 'item', value: 'detail', width: '400px' },
      ],
      currentPage: 1,
      pageSize: 20,
      isLoading: false,
    }
  },
  mounted () {
    this.fetchInventories()
  },
  watch: {
    searchText() {
      this.searchStart()
    }
  },
  computed: {
    ...mapGetters({
      company: 'getCompany',
      isHqFromStore: 'hq/getIsHq'
    }),
    showDialog: {
      get() {
        return this.open
      },
      set(newValue) {
        this.$emit('update:open', newValue)
      }
    },
    allSelected () {
      const selectableItems = this.inventories.data.filter(item => !item.disabled)
      if (selectableItems.length === 0) return false
      const ids = selectableItems.map(item => item.id)
      return this.selected.filter(item => ids.includes(item.id)).length === ids.length
    },
    isHqContext () {
      return this.isHqFromStore
    },
    isStockTransfer () {
      return this.selectedDocument?.id === 'stock-transfer'
    },
    companySlugToUse() {
      return this.overrideCompanySlug || this.company.slug
    },
    params () {
      return {
        page: this.currentPage,
        page_size: this.pageSize,
        status: 'published',
        search: this.searchText,
      }
    },
  },
  methods: {
    ...mapActions([
      'showMessage'
    ]),
    pageChanged () {
      this.fetchInventories()
    },
    searchStart: debounce(function () {
      this.currentPage = 1
      this.fetchInventories()
    }, 750),
    async fetchInventories () {
      try {
        this.inventories.isFetching = true
        const endpoint = `/clinics/${this.companySlugToUse}/inventories`
        const { data } = await this.axios.get(endpoint, { params: this.params })
        this.inventories.totalCount = data.count
        this.inventories.data = data.results.map(result => {
          const row = {
            id: result.id,
            detail: result,
          }
          row.disabled = this.isStockTransfer && !this.hasStock(row)
          return row
        })
      } catch (error) {
        this.inventories.isError = true
        this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
      } finally {
        this.inventories.isFetching = false
      }
    },
    handleSelectAll (value) {
      if (value) {
        const selectable = this.inventories.data.filter(item => !item.disabled)
        this.selected = [...this.selected, ...selectable]
      } else {
        const productsId = this.inventories.data.map(item => item.id)
        this.selected = this.selected.filter(item => !productsId.includes(item.id))
      }
    },
    handleRemoveItem (item) {
      this.selected = this.selected.filter(selectedItem => selectedItem.id !== item.id)
    },
    handleRemoveAllItem () {
      this.selected = []
    },
    handleCloseDialog () {
      this.showDialog = false
    },
    hasStock(item) {
      if (!item.detail.inventory_enabled) return true
      return item.detail.available_qty > 0
    },
    handleAdd () {
      const selected = this.selected.map(item => item.detail)
      this.$emit('added', selected)
    },
    getGroupName (item) {
      return item.detail.groups.map(group => group.value).join(', ')
    },
    rowClass (item) {
      return item.disabled ? 'row-disabled-bg' : ''
    },
  },
}
</script>

<style scoped>
.top-bar {
  position: sticky;
  top: 0;
  background-color: #FFFFFF !important;
  border-color: #FFFFFF !important;
  z-index: 4;
  padding-top: 8px;
}
.row-disabled {
  color: #9ca3af;
}
::v-deep tr.row-disabled-bg,
::v-deep tr.row-disabled-bg:hover,
::v-deep tr.row-disabled-bg.v-data-table__selected {
  background-color: #f5f5f5 !important;
}
</style>